import { FC, useCallback, useMemo } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import {
	DpCarouselIARecommendation,
	IACarouselType,
} from '@/components/blocks/DpCarouselIARecommendation';
import { CatalogEntryIARecommendationSkeleton } from '@/components/content/CatalogEntryIARecommendation/parts/CatalogEntryIARecommendationSkeleton';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { usePersonalizationDppSizes, usePersonalizationSeed } from '@/data/Content/RepurchaseCoupon';
import { personalSizesFromDpp } from '@/data/Content/RecomPersonalSizes';
import { usePersonalizationFamilyPool } from '@/data/Content/RepurchaseFamilyPool';
import { excludeSeedGender, withoutSeedGender } from '@/data/Content/RepurchaseFamilyPoolRules';
import { ProductType } from '@/data/types/Product';
import type { DpIARecommendationType } from '@/data/Content/CatalogEntryIARecommendation';
import { RepurchasePricingEnabledContext } from '@/data/context/RepurchasePricing';
import { fillPersonalization } from '@/components/blocks/DpRepurchaseCoupon/fillPersonalization';
import {
	usePersonalizationPurchase,
	usePersonalizationTokens,
} from '@/components/blocks/DpRepurchaseCoupon/usePersonalizationTokens';
import { RepurchaseSectionHeader } from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseSectionHeader';

const DPP_RECOMMENDATION_TYPES: DpIARecommendationType[] = ['outfit', 'complements', 'sameBrand'];

const DPP_FAMILY_RECOMMENDATION_TYPES: DpIARecommendationType[] = [
	...DPP_RECOMMENDATION_TYPES,
	'family',
];

const CLICK_SOURCE_BY_TYPE: Record<IACarouselType, CLICK_PRODUCT_VIEW_SOURCE> = {
	outfit: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_OUTFIT,
	complements: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_COMPLEMENTS,
	recommendation: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_RECOMMENDED,
	sameBrand: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_SAME_BRAND,
	family: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_FAMILY,
};

type FamilyPoolAttributes = {
	spothombre?: string;
	spotmujer?: string;
	spotnino?: string;
	poolsize?: string;
	familypriority?: string;
};

const useDppRecommendationRequest = (
	type: IACarouselType,
	{ spothombre, spotmujer, spotnino, poolsize, familypriority }: FamilyPoolAttributes
) => {
	const isFamily = type === 'family';
	const { gender, loading: loadingPurchase } = usePersonalizationPurchase();
	const { partNumbers, loading } = usePersonalizationFamilyPool({
		spots:
			isFamily && !loadingPurchase
				? excludeSeedGender({ hombre: spothombre, mujer: spotmujer, nino: spotnino }, gender)
				: {},
		size: Number(poolsize) || undefined,
		priority: familypriority,
	});
	const productsSelector = useCallback(
		(products: ProductType[]) => withoutSeedGender(products, gender),
		[gender]
	);

	return isFamily
		? {
				types: DPP_FAMILY_RECOMMENDATION_TYPES,
				seedPartNumbers: partNumbers,
				productsSelector,
				loading: loadingPurchase || loading,
				empty: !partNumbers.length,
		  }
		: { types: DPP_RECOMMENDATION_TYPES, loading: false, empty: false };
};

const dppCarouselSX: SxProps<Theme> = {
	'& > :last-child': { marginBottom: { xs: '24px', sm: '40px', md: 0 } },
};

export const DpCarouselDppIARecommendation: FC<{
		id: string;
		title?: string;
		type?: IACarouselType;
		transform: unknown;
		filters: unknown;
		eyebrow?: string;
		subtitle?: string;
		chip?: string;
		anchor?: string;
	} & FamilyPoolAttributes
> = ({
	id,
	title: rawTitle,
	type = 'recommendation',
	transform,
	filters,
	eyebrow,
	subtitle,
	chip,
	anchor,
	...familyPoolAttributes
}) => {
	const { partNumber, loading: loadingSeed } = usePersonalizationSeed();
	const { dppSizes, loading: loadingSizes } = usePersonalizationDppSizes();
	const sizes = useMemo(() => personalSizesFromDpp(dppSizes), [dppSizes]);
	const request = useDppRecommendationRequest(type, familyPoolAttributes);
	const tokens = usePersonalizationTokens();
	const params = useMemo(() => (partNumber ? { partNumber } : null), [partNumber]);
	const title = fillPersonalization(rawTitle, tokens) || undefined;
	const header = title ? (
		<RepurchaseSectionHeader
			anchor={anchor}
			eyebrow={fillPersonalization(eyebrow, tokens)}
			title={title}
			subtitle={fillPersonalization(subtitle, tokens)}
			chip={fillPersonalization(chip, tokens)}
		/>
	) : undefined;

	if (loadingSeed || loadingSizes || request.loading) {
		return <CatalogEntryIARecommendationSkeleton id={id} title={title} />;
	}

	if (!params || request.empty) {
		return null;
	}

	return (
		<RepurchasePricingEnabledContext.Provider value>
			<Box sx={dppCarouselSX}>
				<DpCarouselIARecommendation
					id={id}
					type={type}
					title={title}
					params={params}
					transform={transform}
					filters={filters}
					types={request.types}
					seedPartNumbers={request.seedPartNumbers}
					productsSelector={request.productsSelector}
					sizes={sizes}
					sizeMode="prefer"
					withoutIdentity
					clickSource={CLICK_SOURCE_BY_TYPE[type]}
					header={header}
					minProducts={1}
					excludeFromCount={partNumber}
					pickMostAvailableOutfit
					source="recom"
				/>
			</Box>
		</RepurchasePricingEnabledContext.Provider>
	);
};
